<template>
  <div class="usage-analytics-panel">
    <div class="panel">
      <div class="panel-title">
        {{ $t('hit-admin.subscriptions.usageAnalytics.historicalTitle') }}
      </div>
      <div class="panel-subtitle">
        {{ $t('hit-admin.subscriptions.usageAnalytics.historicalSubtitle') }}
      </div>

      <div class="panel-grid">
        <hit-select
          class="min-w-[32rem] w-full"
          :label="$t('hit-admin.subscriptions.usageAnalytics.company')"
          :options="companyOptions"
          :value="selectedCompanyId"
          item-id-property="id"
          :label-fields="['label']"
          filterable
          clearable
          @change="onCompanyChange"
        />

        <hit-select
          class="min-w-[12rem]"
          :label="$t('hit-admin.subscriptions.usageAnalytics.months')"
          :options="monthOptions"
          :value="selectedMonths"
          item-id-property="id"
          :label-fields="['label']"
          clearable="false"
          @change="onMonthsChange"
        />
      </div>

      <div class="toolbar">
        <hit-button
          :label="$t('hit-admin.subscriptions.usageAnalytics.refresh')"
          color="button"
          prefix-icon="refresh"
          :disabled="loading"
          @click="loadData"
        />
      </div>

      <div v-if="errorMessage" class="alert alert-error">
        {{ errorMessage }}
      </div>

      <div v-if="loading" class="loading-state">
        {{ $t('hit-admin.subscriptions.usageAnalytics.loading') }}
      </div>

      <template v-else>
        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-label">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiLatestAmount') }}
            </div>
            <div class="kpi-value">
              {{ formatCurrency(summary.latest_total_amount) }}
            </div>
            <div class="kpi-sub">
              {{ formatPeriodLabel(summary.latest_period) }}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiAvgMonthly') }}
            </div>
            <div class="kpi-value">
              {{ formatCurrency(summary.avg_monthly_amount) }}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiBilledUsers') }}
            </div>
            <div class="kpi-value">{{ summary.latest_billed_users_count }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiCheckins') }}
            </div>
            <div class="kpi-value">
              {{ summary.latest_nsso_checkin_quantity || 0 }}
            </div>
            <div class="kpi-sub">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiSms') }}:
              {{ summary.latest_nsso_sms_quantity || 0 }}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiMomChange') }}
            </div>
            <div class="kpi-value">
              {{ formatPercent(summary.latest_mom_amount_change_pct) }}
            </div>
            <div class="kpi-sub">
              {{ $t('hit-admin.subscriptions.usageAnalytics.kpiModules') }}:
              {{ summary.latest_distinct_modules_count }}
            </div>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-title">
            {{ $t('hit-admin.subscriptions.usageAnalytics.amountChartTitle') }}
          </div>
          <div v-if="amountChartRows.length > 0" class="chart-legend">
            <span
              v-for="item in amountTypeLegend"
              :key="item.key"
              class="legend-item"
            >
              <span class="legend-swatch" :class="`seg-${item.key}`" />
              {{ item.label }}
            </span>
          </div>
          <div v-if="amountChartRows.length === 0" class="chart-empty">
            {{ $t('hit-admin.subscriptions.usageAnalytics.noData') }}
          </div>
          <div v-else class="bars">
            <div
              v-for="row in amountChartRows"
              :key="row.period"
              class="bar-row"
            >
              <div class="bar-label">{{ row.period_label }}</div>
              <div class="bar-track stacked">
                <div
                  v-for="seg in row.segments"
                  :key="seg.key"
                  class="bar-fill"
                  :class="`seg-${seg.key}`"
                  :style="{width: `${seg.widthPct}%`}"
                  :title="`${seg.label}: ${formatCurrency(seg.amount)}`"
                />
              </div>
              <div class="bar-value">
                {{ formatCurrency(row.total_amount) }}
              </div>
            </div>
          </div>
        </div>

        <div class="charts-row">
          <div class="chart-card">
            <div class="chart-title">
              {{ $t('hit-admin.subscriptions.usageAnalytics.usersChartTitle') }}
            </div>
            <div v-if="usersChartRows.length === 0" class="chart-empty">
              {{ $t('hit-admin.subscriptions.usageAnalytics.noData') }}
            </div>
            <div v-else class="bars">
              <div
                v-for="row in usersChartRows"
                :key="`users-${row.period}`"
                class="bar-row"
              >
                <div class="bar-label">{{ row.period_label }}</div>
                <div class="bar-track">
                  <div
                    class="bar-fill users"
                    :style="{width: `${row.widthPct}%`}"
                  />
                </div>
                <div class="bar-value">{{ row.billed_users_count }}</div>
              </div>
            </div>
          </div>

          <div class="chart-card">
            <div class="chart-title">
              {{ $t('hit-admin.subscriptions.usageAnalytics.typeChartTitle') }}
            </div>
            <div v-if="!latestPeriodRow" class="chart-empty">
              {{ $t('hit-admin.subscriptions.usageAnalytics.noData') }}
            </div>
            <div v-else class="bars">
              <div
                v-for="row in typeBreakdownRows"
                :key="row.key"
                class="bar-row"
              >
                <div class="bar-label">{{ row.label }}</div>
                <div class="bar-track">
                  <div
                    class="bar-fill type"
                    :style="{width: `${row.widthPct}%`}"
                  />
                </div>
                <div class="bar-value">{{ formatCurrency(row.amount) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-title">
            {{
              $t('hit-admin.subscriptions.usageAnalytics.checkinsChartTitle')
            }}
          </div>
          <div v-if="checkinChartRows.length === 0" class="chart-empty">
            {{ $t('hit-admin.subscriptions.usageAnalytics.noData') }}
          </div>
          <div v-else class="bars">
            <div
              v-for="row in checkinChartRows"
              :key="`checkins-${row.period}`"
              class="bar-row bar-row-wide"
            >
              <div class="bar-label">{{ row.period_label }}</div>
              <div class="bar-track">
                <div
                  class="bar-fill users"
                  :style="{width: `${row.widthPct}%`}"
                />
              </div>
              <div class="bar-value">
                {{ row.nsso_checkin_quantity || 0 }}
                ·
                {{
                  $t('hit-admin.subscriptions.usageAnalytics.smsBeside', {
                    count: row.nsso_sms_quantity || 0,
                  })
                }}
              </div>
            </div>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-title">
            {{ $t('hit-admin.subscriptions.usageAnalytics.topModulesTitle') }}
          </div>
          <div v-if="topModules.length === 0" class="chart-empty">
            {{ $t('hit-admin.subscriptions.usageAnalytics.noData') }}
          </div>
          <table v-else class="data-table">
            <thead>
              <tr>
                <th>
                  {{
                    $t('hit-admin.subscriptions.usageAnalytics.columns.module')
                  }}
                </th>
                <th class="col-num">
                  {{
                    $t(
                      'hit-admin.subscriptions.usageAnalytics.columns.quantity',
                    )
                  }}
                </th>
                <th class="col-num">
                  {{
                    $t('hit-admin.subscriptions.usageAnalytics.columns.amount')
                  }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in topModules" :key="row.module_id">
                <td>{{ formatModuleLabel(row.designation_key) }}</td>
                <td class="col-num">{{ row.quantity }}</td>
                <td class="col-num">{{ formatCurrency(row.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitSelect} from '@hit/components';
import {adminApi, COMPANY_OPTIONS_MAX_LIMIT} from '@admin/services/adminApi';

const EMPTY_SUMMARY = {
  latest_period: null,
  latest_total_amount: 0,
  avg_monthly_amount: 0,
  avg_billed_companies: 0,
  latest_billed_users_count: 0,
  latest_distinct_modules_count: 0,
  latest_nsso_checkin_quantity: 0,
  latest_nsso_sms_quantity: 0,
  latest_mom_amount_change_pct: null,
};

export default defineComponent({
  name: 'HistoricalUsagePanel',
  components: {
    HitButton,
    HitSelect,
  },
  data() {
    return {
      companyOptions: [],
      selectedCompanyId: null,
      selectedMonths: 12,
      loading: false,
      errorMessage: '',
      periods: [],
      summary: {...EMPTY_SUMMARY},
      topModules: [],
    };
  },
  computed: {
    monthOptions() {
      return [6, 12, 18, 24].map((value) => ({
        id: value,
        label: this.$t('hit-admin.subscriptions.usageAnalytics.monthsOption', {
          count: value,
        }),
      }));
    },
    amountTypeLegend() {
      return [
        {
          key: 'module',
          label: this.$t('hit-admin.subscriptions.usageAnalytics.typeModule'),
        },
        {
          key: 'peppol',
          label: this.$t('hit-admin.subscriptions.usageAnalytics.typePeppol'),
        },
        {
          key: 'company_service',
          label: this.$t(
            'hit-admin.subscriptions.usageAnalytics.typeCompanyService',
          ),
        },
      ];
    },
    amountChartRows() {
      const max = Math.max(...this.periods.map((p) => p.total_amount), 0);
      const pct = (amount) =>
        max > 0 ? Math.round((Number(amount || 0) / max) * 100) : 0;
      return this.periods.map((row) => ({
        ...row,
        segments: [
          {
            key: 'module',
            label: this.$t('hit-admin.subscriptions.usageAnalytics.typeModule'),
            amount: row.module_amount,
            widthPct: pct(row.module_amount),
          },
          {
            key: 'peppol',
            label: this.$t('hit-admin.subscriptions.usageAnalytics.typePeppol'),
            amount: row.peppol_amount,
            widthPct: pct(row.peppol_amount),
          },
          {
            key: 'company_service',
            label: this.$t(
              'hit-admin.subscriptions.usageAnalytics.typeCompanyService',
            ),
            amount: row.company_service_amount,
            widthPct: pct(row.company_service_amount),
          },
        ],
      }));
    },
    usersChartRows() {
      const max = Math.max(...this.periods.map((p) => p.billed_users_count), 0);
      return this.periods.map((row) => ({
        ...row,
        widthPct:
          max > 0 ? Math.round((row.billed_users_count / max) * 100) : 0,
      }));
    },
    checkinChartRows() {
      const max = Math.max(
        ...this.periods.map((p) => p.nsso_checkin_quantity || 0),
        0,
      );
      return this.periods.map((row) => ({
        ...row,
        widthPct:
          max > 0
            ? Math.round(((row.nsso_checkin_quantity || 0) / max) * 100)
            : 0,
      }));
    },
    latestPeriodRow() {
      return this.periods.length ? this.periods[this.periods.length - 1] : null;
    },
    typeBreakdownRows() {
      const row = this.latestPeriodRow;
      if (!row) return [];
      const items = [
        {
          key: 'module',
          label: this.$t('hit-admin.subscriptions.usageAnalytics.typeModule'),
          amount: row.module_amount,
        },
        {
          key: 'peppol',
          label: this.$t('hit-admin.subscriptions.usageAnalytics.typePeppol'),
          amount: row.peppol_amount,
        },
        {
          key: 'company_service',
          label: this.$t(
            'hit-admin.subscriptions.usageAnalytics.typeCompanyService',
          ),
          amount: row.company_service_amount,
        },
      ];
      const max = Math.max(...items.map((item) => item.amount), 0);
      return items.map((item) => ({
        ...item,
        widthPct: max > 0 ? Math.round((item.amount / max) * 100) : 0,
      }));
    },
  },
  async mounted() {
    await this.loadCompanyOptions();
    await this.loadData();
  },
  methods: {
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({
          limit: COMPANY_OPTIONS_MAX_LIMIT,
        });
        this.companyOptions = (res.rows || []).map((c) => ({
          id: c.id,
          label: [c.designation, c.vat_no].filter(Boolean).join(' — ') || c.id,
        }));
      } catch {
        this.companyOptions = [];
      }
    },
    onCompanyChange(value) {
      this.selectedCompanyId = value || null;
      this.loadData();
    },
    onMonthsChange(value) {
      if (value) this.selectedMonths = value;
      this.loadData();
    },
    async loadData() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const res = await adminApi.getUsageHistory({
          months: this.selectedMonths,
          company_id: this.selectedCompanyId,
        });
        this.periods = res?.periods || [];
        this.summary = {...EMPTY_SUMMARY, ...(res?.summary || {})};
        this.topModules = res?.top_modules_latest || [];
      } catch (e) {
        this.periods = [];
        this.summary = {...EMPTY_SUMMARY};
        this.topModules = [];
        this.errorMessage =
          e?.message ||
          this.$t('hit-admin.subscriptions.usageAnalytics.loadError');
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      const amount = Number(value || 0);
      return new Intl.NumberFormat('fr-BE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    formatPercent(value) {
      if (value === null || value === undefined) return '—';
      const sign = value > 0 ? '+' : '';
      return `${sign}${Number(value).toFixed(1)} %`;
    },
    formatPeriodLabel(period) {
      if (!period || period.length !== 6) return '—';
      return `${period.slice(0, 4)}-${period.slice(4)}`;
    },
    formatModuleLabel(key) {
      if (!key) return '—';
      return key.replace(/^module\./, '').replace(/_/g, ' ');
    },
  },
});
</script>

<style lang="scss" scoped>
.usage-analytics-panel {
  height: 100%;
  min-height: 0;
}

.panel {
  display: grid;
  gap: 0.75rem;
  padding: 0.75rem;
}

.panel-title {
  font-size: 1rem;
  font-weight: 650;
  color: rgba(15, 23, 42, 0.92);
}

.panel-subtitle {
  font-size: 0.82rem;
  color: rgba(71, 85, 105, 1);
}

.panel-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.toolbar {
  display: flex;
  gap: 0.5rem;
}

.alert-error,
.loading-state,
.chart-empty {
  font-size: 0.82rem;
  color: rgba(100, 116, 139, 1);
}

.alert-error {
  color: #b91c1c;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
}

.kpi-card,
.chart-card {
  border: 1px solid var(--ho-border, rgba(15, 23, 42, 0.08));
  background: #fff;
  border-radius: 0.75rem;
  padding: 0.7rem 0.85rem;
}

.kpi-label {
  font-size: 0.75rem;
  color: rgba(100, 116, 139, 1);
}

.kpi-value {
  margin-top: 0.3rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
}

.kpi-sub {
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: rgba(71, 85, 105, 1);
}

.charts-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.chart-title {
  font-size: 0.82rem;
  font-weight: 650;
  color: rgba(15, 23, 42, 0.82);
  margin-bottom: 0.45rem;
}

.bars {
  display: grid;
  gap: 0.35rem;
}

.bar-row {
  display: grid;
  grid-template-columns: 6rem minmax(0, 1fr) 6rem;
  align-items: center;
  gap: 0.5rem;
}

.bar-row-wide {
  grid-template-columns: 6rem minmax(0, 1fr) 8.5rem;
}

.bar-label {
  font-size: 0.75rem;
  color: rgba(51, 65, 85, 1);
}

.bar-track {
  height: 0.65rem;
  background: rgba(226, 232, 240, 0.9);
  border-radius: 999px;
  overflow: hidden;
}

.bar-track.stacked {
  display: flex;
}

.bar-fill {
  height: 100%;
  border-radius: 999px;
  flex-shrink: 0;
}

.bar-track.stacked .bar-fill {
  border-radius: 0;
}

.bar-fill.amount {
  background: #2c5aa0;
}

.bar-fill.users {
  background: #0f766e;
}

.bar-fill.type {
  background: #7c3aed;
}

.bar-fill.seg-module,
.legend-swatch.seg-module {
  background: #2c5aa0;
}

.bar-fill.seg-peppol,
.legend-swatch.seg-peppol {
  background: #d97706;
}

.bar-fill.seg-company_service,
.legend-swatch.seg-company_service {
  background: #7c3aed;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.55rem;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: rgba(51, 65, 85, 1);
}

.legend-swatch {
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 0.15rem;
  flex-shrink: 0;
}

.bar-value {
  font-size: 0.75rem;
  text-align: right;
  color: rgba(15, 23, 42, 0.88);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.data-table th,
.data-table td {
  padding: 0.45rem 0.35rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  text-align: left;
}

.col-num {
  text-align: right;
}

@media (max-width: 1100px) {
  .kpi-grid,
  .charts-row {
    grid-template-columns: 1fr;
  }
}
</style>
